import { z } from "zod";

export const CampaignStatusSchema = z.enum(["draft", "sending", "sent", "failed"]);
export type CampaignStatus = z.infer<typeof CampaignStatusSchema>;

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  draft: "Draft",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
};

// Badge variant per status — mirrors the quote status convention.
export const CAMPAIGN_STATUS_BADGE_VARIANT: Record<
  CampaignStatus,
  "default" | "success" | "warning" | "danger" | "info" | "secondary"
> = {
  draft: "secondary",
  sending: "info",
  sent: "success",
  failed: "danger",
};

// Segment kinds mirror contact kind, but are deliberately typed as string[]
// here (not ContactKind[]) — the contract spells the field as `kinds?:string[]`
// on Campaign.segment, decoupled from the contacts feature's enum so a
// segment saved before a future contact-kind addition still round-trips.
export const SegmentSchema = z.object({
  kinds: z.array(z.string()).optional(),
  warm: z.string().nullable().optional(),
  // Contract ambiguity: `segment-preview` is documented as
  // `?kinds=&warm=&tags=` with no consent query param, i.e. the consent
  // gate is always applied server-side and is not a user-editable filter.
  // We still round-trip this field on the segment object (server sets it),
  // but the builder UI never exposes a toggle for it — see
  // CampaignBuilderDialog's segment fields.
  has_email_consent: z.boolean().nullable().optional(),
  tags: z.array(z.string()).optional(),
});
export type Segment = z.infer<typeof SegmentSchema>;

export const CampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  subject: z.string(),
  body_template: z.string(),
  segment: SegmentSchema,
  status: CampaignStatusSchema,
  personalize: z.boolean(),
  sent_at: z.string().nullable(),
  created_at: z.string(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CampaignListResponseSchema = z.object({
  items: z.array(CampaignSchema),
  next_cursor: z.string().nullable(),
});
export type CampaignListResponse = z.infer<typeof CampaignListResponseSchema>;

// GET /campaigns/{id} returns the campaign plus a recipients_summary block.
// Its exact shape isn't spelled out in the contract beyond "recipients
// summary" — modeled as an eligible-count with a passthrough so additional
// server fields (e.g. a sample list) don't fail validation.
export const RecipientsSummarySchema = z
  .object({
    count: z.number(),
  })
  .passthrough();
export type RecipientsSummary = z.infer<typeof RecipientsSummarySchema>;

export const CampaignDetailSchema = CampaignSchema.extend({
  recipients_summary: RecipientsSummarySchema.nullable().optional(),
});
export type CampaignDetail = z.infer<typeof CampaignDetailSchema>;

// ---- Segment preview -----------------------------------------------------

export const SegmentPreviewContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  kind: z.string(),
});
export type SegmentPreviewContact = z.infer<typeof SegmentPreviewContactSchema>;

export const SegmentPreviewSchema = z.object({
  count: z.number(),
  sample: z.array(SegmentPreviewContactSchema),
});
export type SegmentPreview = z.infer<typeof SegmentPreviewSchema>;

// ---- Preview (rendered email, one recipient) -----------------------------

export const CampaignPreviewResultSchema = z.object({
  subject: z.string(),
  body_html: z.string(),
});
export type CampaignPreviewResult = z.infer<typeof CampaignPreviewResultSchema>;

// ---- Send result ----------------------------------------------------------

export const CampaignSendResultSchema = z.object({
  campaign_id: z.string(),
  total: z.number(),
  sent: z.number(),
  skipped: z.number(),
  failed: z.number(),
});
export type CampaignSendResult = z.infer<typeof CampaignSendResultSchema>;

// ---- Stats ------------------------------------------------------------

export const CampaignStatsSchema = z.object({
  total: z.number(),
  sent: z.number(),
  opened: z.number(),
  clicked: z.number(),
  failed: z.number(),
  open_rate: z.number(),
  click_rate: z.number(),
});
export type CampaignStats = z.infer<typeof CampaignStatsSchema>;

// ---- Forms / mutation inputs ------------------------------------------

export interface CreateCampaignInput {
  name: string;
  subject: string;
  body_template: string;
  segment: Segment;
  personalize: boolean;
}

export interface PatchCampaignInput {
  id: string;
  name?: string;
  subject?: string;
  body_template?: string;
  segment?: Segment;
  personalize?: boolean;
}
